import React from "react";
import Card from "components/card";
import Button from "components/button/Button";
import CommonModal from "components/modals/Modal";
import { createPortal } from "react-dom";
import { useDisclosure } from "@chakra-ui/hooks";
import { useNavigate } from "react-router-dom";
import { numericFormatter } from "react-number-format";
import { FaShippingFast, FaTicketAlt, FaTrash } from "react-icons/fa";
import {
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  ModalHeader,
} from "@chakra-ui/modal";
import {
  CouponType,
  DiscountType,
  ICoupons,
} from "views/admin/coupons/types/coupons.interface";
import { MdDiscount } from "react-icons/md";
import { Badge } from "@chakra-ui/react";

interface ICouponCardProps extends ICoupons {
  onDelete?: (slug: string) => void;
}

const CouponCard: React.FC<ICouponCardProps> = (props) => {
  const navigate = useNavigate();
  const {
    name,
    code,
    image,
    start_at,
    end_at,
    description,
    discount,
    discount_type,
    minimum_transaction,
    slug,
    quota,
    type,
    users,
    is_private,
    onDelete,
  } = props;
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [modalAction, setModalAction] = React.useState<
    "show" | "confirmation"
  >();
  const [imageTarget] = React.useState<string>("");

  return (
    <>
      <Card extra={`flex flex-col w-full h-full !p-4 3xl:p-![18px] bg-white`}>
        <div className="h-full w-full">
          <div className="relative w-full overflow-hidden">
            {image ? (
              <img
                src={
                  image
                    ? image.includes("https://")
                      ? image
                      : `${process.env.REACT_APP_PUBLIC_URL}/${image}`
                    : "https://placehold.co/600x400?text=No+Thumbnail"
                }
                className="mb-3 h-56 w-full rounded-xl object-cover object-center"
                alt=""
              />
            ) : (
              <div className="flex h-56 w-full items-center justify-center rounded-xl bg-brand-50 bg-opacity-25">
                <FaTicketAlt className="h-12 w-12 text-brand-500" />
              </div>
            )}
            {is_private && (
              <div className="absolute -left-[121px] top-6 z-10 flex h-7 w-full -rotate-45 cursor-pointer items-center justify-center whitespace-nowrap rounded-tl-xl rounded-tr-xl bg-brand-500 font-medium text-white shadow-md lg:-left-[100px] xl:-left-[105px] 3xl:-left-36 4xl:-left-48">
                Kupon Privat
              </div>
            )}
          </div>

          <div className="mb-3 flex items-center justify-between px-1 md:flex-col md:items-start lg:flex-row lg:justify-between xl:flex-col xl:items-start 3xl:flex-row 3xl:justify-between">
            <div className="flex w-full">
              <div className="mb-2 flex flex-col overflow-hidden">
                <p className="truncate text-lg font-bold text-navy-700 dark:text-white">
                  {" "}
                  {name}
                </p>
                <div className="flex flex-col">
                  <p className="mt-1 text-sm font-medium text-gray-600">
                    Berlaku :{" "}
                    <span className="text-brand-500">
                      {new Date(start_at).toLocaleDateString("id-ID", {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                        hour: "numeric",
                        minute: "numeric",
                        timeZone: "Asia/Jakarta",
                        timeZoneName: "short",
                      })}{" "}
                      -{" "}
                      {new Date(end_at).toLocaleDateString("id-ID", {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                        hour: "numeric",
                        minute: "numeric",
                        timeZone: "Asia/Jakarta",
                        timeZoneName: "short",
                      })}{" "}
                    </span>
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between md:flex-col md:items-start lg:flex-row lg:justify-between xl:flex-col 2xl:items-start 3xl:flex-row 3xl:items-center 3xl:justify-between">
            <div className="flex">
              <p className="mb-2 text-lg font-bold text-navy-700 dark:text-white">
                {code}
              </p>
            </div>
            <div className="flex items-center justify-between gap-2">
              <Button
                size="fit"
                children={
                  <div className="flex h-5 items-center">
                    <FaTrash className="text-xs text-white" />
                  </div>
                }
                variant="danger"
                onClick={() => {
                  setModalAction("confirmation");
                  onOpen();
                }}
              />
              <Button
                size="fit"
                children="Edit"
                variant="gray"
                onClick={() => {
                  navigate(`/admin/coupons/edit/${slug}`);
                }}
              />
              <Button
                size="fit"
                children="Detail"
                variant="primary"
                onClick={() => {
                  setModalAction("show");
                  onOpen();
                }}
              />
            </div>
          </div>
        </div>
      </Card>
      {createPortal(
        <CommonModal
          size="2xl"
          children={
            <div>
              <ModalHeader>
                {modalAction === "show" ? "Detail Kupon" : "Konfirmasi"}
              </ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                {modalAction === "show" ? (
                  <div className={`flex h-full w-full flex-col bg-white`}>
                    <div className="h-full w-full">
                      <div className="relative mb-4 w-full overflow-hidden">
                        {image ? (
                          <img
                            src={
                              image
                                ? image.includes("https://")
                                  ? image
                                  : `${process.env.REACT_APP_PUBLIC_URL}/${
                                      imageTarget !== "" ? imageTarget : image
                                    }`
                                : "https://placehold.co/600x400?text=No+Thumbnail"
                            }
                            className="mb-3 h-full w-full rounded-xl object-cover object-center 3xl:h-full 3xl:w-full"
                            alt=""
                          />
                        ) : (
                          <div className="flex h-56 w-full items-center justify-center rounded-xl bg-brand-50 bg-opacity-25">
                            <FaTicketAlt className="h-12 w-12 text-brand-500" />
                          </div>
                        )}
                      </div>

                      {users.length > 0 ? (
                        <Badge
                          colorScheme="green"
                          className="mb-1 w-fit"
                          fontSize="0.8rem"
                        >
                          Kupon Privat
                        </Badge>
                      ) : (
                        <></>
                      )}

                      <div className="mb-3">
                        <p className="text-lg font-bold text-navy-700 dark:text-white">
                          {" "}
                          {name}{" "}
                        </p>
                      </div>

                      <div className="flex flex-col gap-2">
                        <div className="flex flex-col gap-1">
                          <p className="mt-1 font-medium text-gray-600">
                            Berlaku:
                          </p>
                          <span className="font-medium text-brand-500">
                            {new Date(start_at).toLocaleDateString("id-ID", {
                              year: "numeric",
                              month: "short",
                              day: "numeric",
                              hour: "numeric",
                              minute: "numeric",
                              timeZone: "Asia/Jakarta",
                              timeZoneName: "short",
                            })}{" "}
                            -{" "}
                            {new Date(end_at).toLocaleDateString("id-ID", {
                              year: "numeric",
                              month: "short",
                              day: "numeric",
                              hour: "numeric",
                              minute: "numeric",
                              timeZone: "Asia/Jakarta",
                              timeZoneName: "short",
                            })}{" "}
                          </span>
                        </div>
                        <div className="flex gap-5">
                          <div className="flex flex-col gap-1">
                            <p className="mt-1 text-sm font-medium text-gray-600">
                              Kupon untuk:
                            </p>
                            <p className="font-medium text-navy-700">
                              {type === CouponType.Purchase ? (
                                <span className="flex items-center gap-2">
                                  <MdDiscount className="text-lg text-brand-500" />
                                  Pembelian
                                </span>
                              ) : (
                                <span className="flex items-center gap-2">
                                  <FaShippingFast className="text-lg text-brand-500" />
                                  Pengiriman
                                </span>
                              )}
                            </p>
                          </div>
                          <div className="flex flex-col gap-1">
                            <p className="mt-1 text-sm font-medium text-gray-600">
                              Potongan:
                            </p>
                            <p className="font-medium text-navy-700">
                              {discount_type === DiscountType.Percentage ? (
                                <span>{discount}%</span>
                              ) : (
                                <span>
                                  {numericFormatter(discount.toString(), {
                                    decimalSeparator: ",",
                                    thousandSeparator: ".",
                                    prefix: "Rp",
                                  })}
                                </span>
                              )}
                            </p>
                          </div>
                        </div>
                        <div className="flex flex-col gap-1">
                          <p className="mt-1 text-sm font-medium text-gray-600">
                            Minimum Pembelian:
                          </p>
                          <p className="font-medium text-navy-700">
                            {numericFormatter(
                              minimum_transaction?.toString() || "0",
                              {
                                decimalSeparator: ",",
                                thousandSeparator: ".",
                                prefix: "Rp",
                              }
                            )}
                          </p>
                        </div>
                        <div className="flex flex-col gap-1">
                          <p className="mt-1 text-sm font-medium text-gray-600">
                            Kuota:
                          </p>
                          <p className="font-medium text-navy-700">
                            {quota || "-"}
                          </p>
                        </div>
                        <div className="flex flex-col">
                          <p className="mt-1 text-sm font-medium text-gray-600">
                            Kode:
                          </p>
                          <p className="mb-2 text-lg font-bold text-navy-700 dark:text-white">
                            {code}
                          </p>
                        </div>
                        {users.length > 0 ? (
                          <div className="flex flex-col">
                            <p className="mt-1 text-sm font-medium text-gray-600">
                              Pengguna Kupon:
                            </p>
                            <p className="mb-2 text-lg font-bold text-navy-700 dark:text-white">
                              {users.map((user, index) => {
                                return (
                                  <span key={index}>
                                    {user.name}
                                    {index < users.length - 1 ? ", " : ""}
                                  </span>
                                );
                              })}
                            </p>
                          </div>
                        ) : (
                          <></>
                        )}
                      </div>
                      <p className="mt-1 text-sm font-medium text-gray-600">
                        Deskripsi:
                      </p>
                      <p className="mt-2 text-base text-gray-700 dark:text-white">
                        {description}
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col gap-3">
                    <p className="text-base text-gray-700 dark:text-white">
                      Apakah anda yakin ingin menghapus kupon <br />
                      <span className="text-brand-500">{name}</span>
                    </p>
                  </div>
                )}
              </ModalBody>
              <ModalFooter>
                {modalAction === "show" ? (
                  <Button
                    variant="gray"
                    children="Tutup"
                    size="fit"
                    onClick={onClose}
                  />
                ) : (
                  <div className="flex gap-3">
                    <Button
                      size="fit"
                      children="Batal"
                      variant="gray"
                      onClick={onClose}
                    />
                    <Button
                      size="fit"
                      children={<span className="text-white">Ya, hapus</span>}
                      variant="danger"
                      onClick={() => {
                        onClose();
                        onDelete && onDelete(slug);
                      }}
                    />
                  </div>
                )}
              </ModalFooter>
            </div>
          }
          isOpen={isOpen}
          onClose={onClose}
        />,
        document.body
      )}
    </>
  );
};

export default CouponCard;
